import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import { api } from "../services/api";

interface User {
  nome: string;
  perfil: string;
}

interface AuthContextData {
  user: User | null;
  loading: boolean;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // ✅ Carrega token e usuário do localStorage ao montar (apenas 1x)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch {
        localStorage.removeItem("user");
      }
    }

    setLoading(false);
  }, []);

  // ✅ Login seguro e consistente
  const login = async (email: string, senha: string) => {
    try {
      const response = await api.post("/Auth/login", { email, senha });

      // Ajuste conforme o retorno da sua API
      const token: string = response.data.token;
      const nome: string = response.data.nome ?? "Usuário";
      const perfil: string = response.data.perfil ?? "Visualizador";

      if (!token) {
        throw new Error("Token inválido retornado pela API.");
      }

      const userData: User = { nome, perfil };

      // Salva no localStorage
      //localStorage.setItem("token", token);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(userData));
      

      // Atualiza contexto e cabeçalho global
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(userData);
    } catch (error: any) {
      console.error("Erro ao fazer login:", error);
      const message =
        error?.response?.data?.message ||
        "Falha ao fazer login. Verifique suas credenciais.";
      throw new Error(message);
    }
  };

  // ✅ Logout simples e confiável
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
